import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { addYears, format, parseISO } from "date-fns";

interface DuplicateWarning {
  matchedName: string;
  matchedContactId: string;
}

interface NewBabyPromptProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  babyName: string;
  occasionDate: string; // YYYY-MM-DD
  contactName: string;
  atContactLimit: boolean;
  onAddToCircle: (prefill: { name: string; birthday: string; relationship: string }) => void;
  onDismiss: () => void;
  duplicateWarning?: DuplicateWarning | null;
  onViewExisting?: (contactId: string) => void;
}

const NewBabyPrompt = ({
  open,
  onOpenChange,
  babyName,
  occasionDate,
  contactName,
  atContactLimit,
  onAddToCircle,
  onDismiss,
  duplicateWarning,
  onViewExisting,
}: NewBabyPromptProps) => {
  const displayName = babyName || "the baby";
  const firstBirthday = addYears(parseISO(occasionDate), 1);
  const firstBirthdayStr = format(firstBirthday, "MMM d, yyyy");
  const firstBirthdayDate = format(firstBirthday, "yyyy-MM-dd");

  const handleAdd = () => {
    onAddToCircle({
      name: babyName,
      birthday: firstBirthdayDate,
      relationship: "Child",
    });
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-sm text-center">
        <AlertDialogHeader>
          <div className="mb-2 text-4xl">👶</div>
          <AlertDialogTitle className="font-serif text-xl">
            {atContactLimit
              ? "You've reached your 25-person limit."
              : `Add ${displayName} to your Circle?`}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-sm text-muted-foreground">
            {atContactLimit ? (
              <>
                Upgrade to Circle Plus to add {displayName} and keep growing your Circle.
              </>
            ) : (
              <>
                {contactName}'s little one will have their first birthday on{" "}
                <span className="font-medium text-foreground">{firstBirthdayStr}</span>.
                Want to add them so you never miss it?
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {/* Duplicate warning */}
        {duplicateWarning && !atContactLimit && (
          <div className="mt-3 rounded-2xl border-l-[3px] border-l-amber-400 bg-white p-3 text-left">
            <p className="text-sm text-foreground">
              ⚠️ <strong>{babyName || "This name"}</strong> might already be in your Circle.
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              You have a contact named <strong>{duplicateWarning.matchedName}</strong>.
              Are you sure you want to add them again?
            </p>
            <div className="mt-3 flex gap-2">
              <Button
                size="sm"
                className="flex-1 rounded-full"
                onClick={handleAdd}
              >
                Add anyway
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="flex-1 rounded-full"
                onClick={() => onViewExisting?.(duplicateWarning.matchedContactId)}
              >
                View existing contact
              </Button>
            </div>
          </div>
        )}

        <div className="mt-4 space-y-3">
          {atContactLimit ? (
            <Button className="w-full rounded-full" size="lg" disabled>
              Learn about Circle Plus
            </Button>
          ) : !duplicateWarning ? (
            <Button
              className="w-full rounded-full"
              size="lg"
              onClick={handleAdd}
            >
              Add {displayName} to my Circle
            </Button>
          ) : null}
          <button
            onClick={onDismiss}
            className="w-full text-center text-sm text-muted-foreground hover:text-foreground"
          >
            Maybe later
          </button>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default NewBabyPrompt;
